import { Injectable, Injector, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { TranslocoService } from '@jsverse/transloco';
import { HippoUtilsService } from '@hippo/services/utils';
import { environment } from 'environments/environment';
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { UserService } from '../services/user.service';

/**
 * sessionStorage key under which a failed Cognito → PHP session exchange leaves
 * a human-readable message, so the sign-in page can show why the user bounced
 * back instead of silently landing on the login screen ("logged out" feeling).
 */
export const COGNITO_LOGIN_ERROR_KEY = 'cognitoLoginError';

/** Languages of the Cognito managed login, by app language. */
const COGNITO_LANGUAGES: Record<string, string> = {
    de: 'de',
    en: 'en',
    es: 'es',
    fr: 'fr',
    id: 'id',
    it: 'it',
    ja: 'ja',
    ko: 'ko',
    nl: 'nl',
    pt: 'pt-BR',
    zh: 'zh-CN',
};

@Injectable({ providedIn: 'root' })
export class CognitoAuthService {
    private readonly oidc = inject(OidcSecurityService);
    private readonly authService = inject(AuthService);
    private readonly userService = inject(UserService);
    private readonly injector = inject(Injector);

    private sessionReady: Promise<boolean> | null = null;

    readonly isAuthenticated$ = this.oidc.isAuthenticated$;
    readonly userData$ = this.oidc.userData$;

    login(): void {
        // Localize the Cognito Managed Login (Hosted UI) to the app's active
        // language via the `lang` query param — otherwise it always renders in
        // English (HIP-32). Cognito only supports a limited set of locales,
        // every other released language gets the English page.
        //
        // Lazy lookup — injecting TranslocoService in a field would be a
        // circular dependency: TranslocoService constructs TranslocoHttpLoader,
        // which injects this service to await the Cognito session handshake.
        const transloco = this.injector.get(TranslocoService);
        const lang =
            COGNITO_LANGUAGES[
                transloco.getActiveLang() || transloco.getDefaultLang()
            ] ?? 'en';
        this.oidc.authorize(undefined, {
            customParams: { lang },
        });
    }

    checkAuth(): Observable<unknown> {
        return this.oidc.checkAuth();
    }

    getAccessToken(): Observable<string> {
        return this.oidc.getAccessToken();
    }

    /**
     * Memoized session handshake. Translation/data loads await this so they
     * never hit the PHP backend before the Cognito token has been exchanged
     * for a logged-in PHP session (the backend answers 401 otherwise).
     */
    ensureSession(): Promise<boolean> {
        if (!this.sessionReady) {
            this.sessionReady = this.handleRedirectCallback().catch(error => {
                console.error('Cognito session handshake failed', error);
                return false;
            });
        }
        return this.sessionReady;
    }

    /**
     * Returns true if a Cognito session was just exchanged for a PHP session.
     * Caller is responsible for navigating after this resolves.
     */
    async handleRedirectCallback(): Promise<boolean> {
        const result: any = await firstValueFrom(this.oidc.checkAuth());
        if (!result?.isAuthenticated || !result?.idToken) {
            return false;
        }
        if (
            this.userService.getUser()?.uid &&
            HippoUtilsService.getCookie('PHPSESSID')
        ) {
            return true;
        }
        await this.authService.getPhpSessionCookie();
        try {
            const response: any = await lastValueFrom(
                this.authService.signInWithCognitoToken(result.idToken)
            );
            const user = response?.result;
            if (!user?.uid) {
                this.rememberLoginError(response?.result);
                return false;
            }
            // Mirror the password login (sign-in.component): enrich the user with
            // its system-access flag before persisting, otherwise a Cognito user
            // loses the admin/system-settings UI that gates on user.systemAccess.
            user.systemAccess = await this.userService.getSystemAccessInfo(
                user.id ?? '',
                user.uid ?? ''
            );
            user.backupAccess = await this.userService.getBackupAccessInfo(
                user.id ?? '',
                user.uid ?? ''
            );
            sessionStorage.setItem('user', JSON.stringify(user));
            this.userService.getUser();
            return true;
        } catch (error: any) {
            // The Cognito token is valid but the backend refused the session
            // (e.g. 403 "cognito user not provisioned"). Surface why instead of
            // bouncing silently to /sign-in.
            this.rememberLoginError(error?.error?.result, error?.status);
            return false;
        }
    }

    /**
     * Persists a human-readable reason for a failed Cognito → PHP session
     * exchange so the sign-in page can display it. Called only when a valid
     * Cognito token was present, so normal anonymous loads never set it.
     */
    private rememberLoginError(backendResult?: string, status?: number): void {
        let message =
            'Die Anmeldung ist fehlgeschlagen. Bitte versuche es erneut.';
        if (
            backendResult === 'cognito user not provisioned' ||
            status === 403
        ) {
            message =
                'Dieser AWS-Account ist mit keinem hippo-Benutzer verknüpft. Bitte wende dich an die Administration.';
        } else if (backendResult === 'token invalid' || status === 401) {
            message =
                'Die Anmeldung ist abgelaufen oder ungültig. Bitte melde dich erneut an.';
        }
        try {
            sessionStorage.setItem(COGNITO_LOGIN_ERROR_KEY, message);
        } catch {}
    }

    logout(): void {
        if (typeof window === 'undefined') {
            return;
        }
        // Drop the OIDC library's local token store first. Otherwise a page
        // refresh can re-exchange the still-valid Cognito token (via the
        // memoized ensureSession handshake) and silently log the user back in
        // before the hosted-UI logout redirect below has terminated the
        // Cognito session (HIP-33).
        try {
            this.oidc.logoffLocal();
        } catch {}
        try {
            window.sessionStorage.clear();
        } catch {}
        const { hostedUiDomain, clientId, postLogoutRedirectUri } =
            environment.cognito;
        const logoutUri = encodeURIComponent(postLogoutRedirectUri);
        window.location.href = `${hostedUiDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    }
}
